import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod/v4';
import { api } from '@shared/api';

export const shopifyCatalogSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      sku: z.string().nullable(),
      product: z.object({ id: z.string(), title: z.string(), status: z.string() }),
      inventoryQuantity: z.number().int().nullable(),
      inventoryItem: z.object({ id: z.string(), tracked: z.boolean() }),
    })
  ),
  pageInfo: z.object({ nextCursor: z.string().nullable(), hasNextPage: z.boolean() }),
  fetchedAt: z.iso.datetime(),
  count: z
    .object({ value: z.number().int().nonnegative(), precision: z.enum(['exact', 'at_least']) })
    .nullable()
    .optional(),
});
export type ShopifyCatalogPage = z.infer<typeof shopifyCatalogSchema>;
export type ShopifyCatalogItem = ShopifyCatalogPage['items'][number];
export const SHOPIFY_CATALOG_PAGE_SIZE = 25;
export const shopifyInventoryFilterSchema = z.enum([
  'all',
  'tracked',
  'tracked_available',
  'tracked_zero',
  'untracked',
]);
export type ShopifyInventoryFilter = z.infer<typeof shopifyInventoryFilterSchema>;
const catalogInputSchema = z.strictObject({
  q: z.string().trim().max(120),
  cursor: z.string().min(1).max(4096).optional(),
  includeCount: z.boolean().optional(),
  inventoryFilter: shopifyInventoryFilterSchema.optional(),
});

export async function getShopifyCatalog(
  input: z.infer<typeof catalogInputSchema>,
  signal: AbortSignal
): Promise<ShopifyCatalogPage> {
  const params = catalogInputSchema.parse(input);
  const response = await api.get('/api/v1/shopify-product-variants', {
    params: { ...params, limit: SHOPIFY_CATALOG_PAGE_SIZE },
    signal,
    timeout: 20_000,
  });
  return shopifyCatalogSchema.parse(response);
}
export const shopifyCatalogKeys = {
  page: (
    q: string,
    cursor?: string,
    includeCount = false,
    inventoryFilter: ShopifyInventoryFilter = 'all'
  ) => ['shopify-catalog', { q, cursor, includeCount, inventoryFilter }] as const,
};
export function shopifyCatalogQuery(
  q: string,
  cursor?: string,
  includeCount = false,
  inventoryFilter: ShopifyInventoryFilter = 'all'
) {
  return queryOptions({
    queryKey: shopifyCatalogKeys.page(q, cursor, includeCount, inventoryFilter),
    queryFn: ({ signal }) =>
      getShopifyCatalog(
        {
          q,
          ...(cursor ? { cursor } : {}),
          ...(includeCount ? { includeCount: true } : {}),
          ...(inventoryFilter !== 'all' ? { inventoryFilter } : {}),
        },
        signal
      ),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
